import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Salesperson, SalespersonService } from '@vendasta/sales';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, ReplaySubject } from 'rxjs';
import { startWith } from 'rxjs/operators';
import { PARTNER_ID_INJECTION_TOKEN } from '../core/constants';

const SALESPEOPLE_PAGE_SIZE = 200;

@Injectable({
  providedIn: 'root',
})
export class SalespeopleService {
  private readonly salespeople$$ = new ReplaySubject<Salesperson[]>(1);
  readonly salespeople$: Observable<Salesperson[]>;

  private readonly loading$$ = new BehaviorSubject<boolean>(false);
  readonly loading$ = this.loading$$.asObservable();

  private currentMarketId: string;
  private currentPartnerId: string;

  constructor(
    private readonly salespersonService: SalespersonService,
    private readonly translateService: TranslateService,
    @Inject(PARTNER_ID_INJECTION_TOKEN) private readonly partnerId$: Observable<string>,
  ) {
    this.salespeople$ = this.salespeople$$.asObservable().pipe(startWith([]));
  }

  // Could cause a performance hit if a partner has a lot of salespeople
  async loadSalespeople(partnerId: string, marketId: string, cursor: string): Promise<void> {
    if (this.currentPartnerId !== partnerId || this.currentMarketId !== marketId) {
      if (this.currentPartnerId !== undefined && this.currentMarketId !== undefined) {
        this.salespeople$$.next([]);
      }
      this.currentPartnerId = partnerId;
      this.currentMarketId = marketId;
    }

    this.loading$$.next(true);
    await firstValueFrom(
      combineLatest([
        this.salespersonService.listSalespersons(
          { partnerId: partnerId, marketId: marketId },
          cursor,
          SALESPEOPLE_PAGE_SIZE,
        ),
        this.salespeople$,
      ]),
    )
      .then(([response, curr]) => {
        this.salespeople$$.next(curr.concat(response.results));
        if (response.nextCursor) {
          this.loadSalespeople(partnerId, marketId, response.nextCursor);
        } else {
          this.loading$$.next(false);
        }
      })
      .catch(() => {
        this.translateService.instant('MANAGE_ACCOUNTS.ERROR_LOADING_SALESPEOPLE');
        this.loading$$.next(false);
      });
  }
}
